/**
 * The signed-in club shell.
 *
 * The chrome is a fixed rail down the left: club identity, the sections and
 * the signed-in account all live there, which leaves the content column with
 * no desktop header at all — the data starts at the top of the page, and that
 * is the whole point of the layout. On a phone the rail collapses into a dark
 * top bar with a hamburger and the same nav slides in as a drawer. Identical
 * items, identical badges — only the container changes (SPEC.md §0).
 *
 * Restyled 26 Aug 2026 (owner's direction) into the product's one design
 * language — the rules are at the top of `components/ui.jsx`. This rail is now
 * the same material as the platform consoles' (`ConsoleShell.jsx`) and as the
 * home page's hero: the ink gradient, the gold→teal hairline, square nav rows
 * marked by a gold bar, and the RunnerCrew mark beside the club's own name.
 * A member who belongs to two clubs used to cross a visible design seam every
 * time they switched; now they do not.
 *
 * The club's NAME still leads — this is the club's portal, not the platform's.
 * The mark beside it is what says whose software it is.
 */

import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { bibNo, openLeadEvents } from '../data';
import { useAuth } from '../context/AuthContext';
import { useClub, useClubData } from '../context/ClubContext';
import { ChatFab, ChatPanel } from './ChatPanel';
import { Avatar } from './Avatar';
import { DemoBanner } from './DemoBanner';
import { useBackToClose } from '../hooks/useBackToClose';
import { Icon, RunnerCrewMark } from './ui';

const ADMIN_TABS = [
  ['/a/dashboard', 'Dashboard', 'grid'],
  ['/a/members', 'Members', 'people'],
  ['/a/dues', 'Dues', 'rupee'],
  ['/a/events', 'Events', 'calendar'],
  ['/a/results', 'Results', 'stopwatch'],
  ['/a/volunteering', 'Volunteer', 'heart'],
  ['/a/requests', 'Requests', 'plus'],
  ['/a/messages', 'Messages', 'mail'],
  ['/a/settings', 'Settings', 'gear'],
];

/**
 * No Profile tab — the account block IS the profile entry point, and a second
 * door would cost one of the slots that fit across a phone.
 */
const MEMBER_TABS = [
  ['/m/bib', 'My Bib', 'bib'],
  ['/m/achievements', 'Achievements', 'medal'],
  ['/m/events', 'Events', 'calendar'],
  ['/m/payments', 'Payments', 'rupee'],
  ['/m/volunteer', 'Volunteer', 'heart'],
  ['/m/notices', 'Notices', 'mail'],
];

/** The rail's ink surface — the home hero's gradient, turned vertical. */
const RAIL_SURFACE = 'bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_52%,#0A0E19_100%)]';

/** The gold→teal hairline that separates identity from nav. */
function AccentRule({ className = '' }) {
  return (
    <div className={`h-[2px] flex-none bg-[linear-gradient(90deg,#F6C019,#7FD8C8)] ${className}`} />
  );
}

/** The crimson count that rides a nav item. Round, because a count is a dot. */
function Badge({ count }) {
  if (!count) return null;
  return (
    <span className="grid h-[18px] min-w-[18px] flex-none place-items-center rounded-full bg-flag-500 px-1 font-market text-[9.5px] font-bold tabular-nums text-white">
      {count}
    </span>
  );
}

/**
 * The club wordmark. The name comes from the SELECTED club — this shell serves
 * every club on the platform. A two-word name keeps the gold interpunct;
 * anything else renders as it is.
 */
export function ClubName({ name, className }) {
  const words = (name ?? 'Club').split(' ');
  if (words.length === 2) {
    return (
      <span className={className}>
        {words[0]}
        <span className="text-gold-500">·</span>
        {words[1]}
      </span>
    );
  }
  return <span className={className}>{name}</span>;
}

function Wordmark({ isAdmin, clubName }) {
  return (
    <div className="flex items-center gap-2.5">
      <RunnerCrewMark size={30} className="flex-none" />
      <div className="min-w-0 leading-none">
        <div className="truncate font-market text-[15.5px] font-extrabold tracking-[-0.02em] text-white">
          <ClubName name={clubName} />
        </div>
        <div className="mt-[3px] font-market text-[8.5px] font-bold uppercase tracking-[0.24em] text-gold-500/90">
          {isAdmin ? 'Committee portal' : 'Member portal'}
        </div>
      </div>
    </div>
  );
}

export function AppShell() {
  const { db, role, meId, me, signOut } = useClubData();
  const { memberships, ownedPortals, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  /* A drawer that survives navigation would cover the page you just asked
     for. Close it whenever the route changes. */
  useEffect(() => setNavOpen(false), [location.pathname]);
  // The phone's Back closes the menu instead of leaving the page under it.
  useBackToClose(navOpen, () => setNavOpen(false));

  const isAdmin = role === 'admin';

  /*
   * Events this member has been put in charge of get their own row in the rail,
   * under the standing tabs. They are a JOB, not a section of the portal, so
   * they appear when the committee assigns one and vanish the moment it marks
   * the event completed — see data/lead.js. Committee accounts get none: they
   * can already do everything the head's desk offers.
   */
  const leadTabs = useMemo(
    () =>
      isAdmin
        ? []
        : openLeadEvents(db.events, meId).map((e) => [`/m/lead/${e.id}`, e.title, 'flag']),
    [db.events, meId, isAdmin],
  );
  const tabs = isAdmin ? ADMIN_TABS : [...MEMBER_TABS, ...leadTabs];
  const clubName = db.club?.name ?? 'Club';

  // FR-AU-06: switching clubs is one click from the chrome. Only shown when
  // the account actually has somewhere else to go.
  const canSwitch = memberships.length > 1 || ownedPortals.length > 0 || isSuperAdmin;

  function SwitchClub({ className = '' }) {
    if (!canSwitch) return null;
    return (
      <button
        type="button"
        onClick={() => navigate('/select-club')}
        className={`mt-3.5 inline-flex items-center gap-1.5 border border-white/[.18] bg-white/[.05] px-2.5 py-[5px] font-market text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/60 transition-colors duration-200 hover:border-gold-500/60 hover:bg-gold-500/[.08] hover:text-gold-500 ${className}`}
      >
        ⇄ Switch club
      </button>
    );
  }

  // Requests that need a committee hand: new asks AND completed payments
  // waiting for final activation — including committee-added members who have
  // paid, since their pipeline ends on the same screen. (For a non-admin this
  // is always 0: peers see neither payments nor dues fields.)
  const actionableRequests =
    db.requests.filter((r) => r.status === 'pending' || r.status === 'paid').length +
    db.members.filter(
      (m) =>
        m.status === 'pending' &&
        (m.duesPaidTill || db.payments.some((p) => p.memberId === m.id)),
    ).length;

  /** The bell counts what needs *you*. */
  const bellCount = useMemo(() => {
    if (isAdmin) {
      const openSuggestions = db.messages.filter(
        (m) => m.kind === 'suggestion' && !(m.replies ?? []).length,
      ).length;
      return openSuggestions + actionableRequests;
    }
    return db.messages.filter(
      (m) => (m.to === 'all' || m.to === meId) && !m.readBy.includes(meId ?? ''),
    ).length;
  }, [db.messages, isAdmin, meId, actionableRequests]);

  function goToMessages() {
    navigate(isAdmin ? '/a/messages' : '/m/notices');
    window.scrollTo(0, 0);
  }

  /** The count a given tab shows, if any. */
  function badgeFor(to) {
    if (to === '/a/requests') return actionableRequests;
    if (to === '/a/messages' || to === '/m/notices') return bellCount;
    return 0;
  }

  /** One nav item, shared by the rail and the drawer. */
  function NavItem({ to, label, icon }) {
    return (
      <NavLink
        to={to}
        onClick={() => window.scrollTo(0, 0)}
        className={({ isActive }) =>
          /* `relative` carries the active marker, which is an absolutely
             positioned gold bar rather than a border — a border would shift
             the label by 3px every time the row became active. */
          `relative flex items-center gap-3 py-[13px] pl-5 pr-3.5 font-market text-[13.5px] font-bold tracking-[-0.005em] transition-colors duration-200 ${
            isActive
              ? 'bg-white/[.07] text-white'
              : 'text-white/55 hover:bg-white/[.035] hover:text-white'
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className="absolute inset-y-0 left-0 w-[3px] bg-gold-500" aria-hidden="true" />
            )}
            {/* Every row keeps its icon — the active one is marked by the
                gold bar and the lighter panel, not by swapping in a swatch. */}
            <Icon
              name={icon}
              size={16}
              className={`flex-none ${isActive ? 'text-gold-500' : 'text-white/35'}`}
            />
            {/* `truncate` because a lead tab's label is an event TITLE, which
                is arbitrary text in a 248px rail. `title` keeps the full name
                reachable on hover. */}
            <span className="flex-1 truncate" title={label}>
              {label}
            </span>
            <Badge count={badgeFor(to)} />
          </>
        )}
      </NavLink>
    );
  }

  /**
   * The signed-in account, at the foot of the rail and of the drawer. This is
   * the ONLY place the account lives — there is no desktop top bar — so it has
   * to carry the profile link, notices and sign-out together.
   */
  function AccountBlock() {
    return (
      <div className="border-t border-white/[.09] px-5 py-4">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2.5 p-1 text-left transition-colors duration-200 hover:bg-white/[.06]"
            onClick={() => {
              navigate(isAdmin ? '/a/profile' : '/m/profile');
              window.scrollTo(0, 0);
            }}
            title={isAdmin ? 'Your committee account' : 'Your profile'}
          >
            {me ? (
              <Avatar member={me} className="h-9 w-9 flex-none text-[12px]" />
            ) : (
              <span className="grid h-9 w-9 flex-none place-items-center bg-gold-500 font-market text-[12px] font-extrabold text-ink-800">
                {clubName
                  .split(' ')
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </span>
            )}
            <span className="min-w-0 leading-tight">
              <span className="block truncate font-market text-[13px] font-bold text-white">
                {me ? me.name : 'Committee'}
              </span>
              <span className="block font-market text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">
                {isAdmin ? 'Committee' : `Bib ${bibNo(me?.bib)}`}
              </span>
            </span>
          </button>

          <button
            type="button"
            className="relative flex-none p-2 text-white/45 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
            onClick={goToMessages}
            title="Notices"
          >
            <Icon name="bell" size={16} strokeWidth={2} />
            {bellCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-[16px] min-w-[16px] place-items-center rounded-full bg-flag-500 px-1 font-market text-[8.5px] font-bold text-white">
                {bellCount}
              </span>
            )}
          </button>

          <button
            type="button"
            className="flex-none p-2 text-white/45 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
            onClick={() => {
              signOut();
              navigate('/signin', { replace: true });
              window.scrollTo(0, 0);
            }}
            title="Sign out"
          >
            <Icon name="signout" size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ---------- the rail: desktop only ---------- */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 hidden w-[248px] flex-col border-r border-white/[.06] lg:flex ${RAIL_SURFACE}`}
      >
        <div className="px-5 pb-5 pt-7">
          <Wordmark isAdmin={isAdmin} clubName={clubName} />
          <SwitchClub />
        </div>
        <AccentRule className="mx-5" />

        <nav className="flex-1 overflow-y-auto py-4">
          {tabs.map(([to, label, icon]) => (
            <NavItem key={to} to={to} label={label} icon={icon} />
          ))}
        </nav>

        <AccountBlock />
      </aside>

      {/* ---------- the drawer: phone only ---------- */}
      {navOpen && (
        <div
          className="fixed inset-0 z-[60] bg-ink-900/70 backdrop-blur-sm lg:hidden"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setNavOpen(false);
          }}
          role="presentation"
        >
          <aside
            className={`flex h-full w-[272px] max-w-[82vw] flex-col motion-safe:animate-slidein ${RAIL_SURFACE}`}
            role="dialog"
            aria-label="Menu"
          >
            <div className="flex items-start justify-between px-5 pb-4 pt-6">
              <div className="min-w-0">
                <Wordmark isAdmin={isAdmin} clubName={clubName} />
                <SwitchClub />
              </div>
              <button
                type="button"
                className="-mr-1 flex-none p-1.5 text-white/50 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
                onClick={() => setNavOpen(false)}
                title="Close menu"
              >
                <Icon name="close" size={17} strokeWidth={2.2} />
              </button>
            </div>
            <AccentRule className="mx-5" />

            <nav className="flex-1 overflow-y-auto py-3">
              {tabs.map(([to, label, icon]) => (
                <NavItem key={to} to={to} label={label} icon={icon} />
              ))}
            </nav>

            <AccountBlock />
          </aside>
        </div>
      )}

      {/* ---------- the content column ---------- */}
      <div className="flex min-h-screen flex-col bg-ground lg:pl-[248px]">
        {/* The phone identity bar. There is deliberately NO desktop header:
            the rail carries identity and account, so the content column can
            start with the data. */}
        <header className={`sticky top-0 z-30 lg:hidden ${RAIL_SURFACE}`}>
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              type="button"
              className="-ml-1.5 p-1.5 text-white/70 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
              onClick={() => setNavOpen(true)}
              title="Menu"
            >
              <Icon name="menu" size={20} strokeWidth={2.2} />
            </button>

            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2.5 text-left leading-none"
              onClick={() => canSwitch && navigate('/select-club')}
              title={canSwitch ? 'Switch club' : undefined}
            >
              <RunnerCrewMark size={26} className="flex-none" />
              <span className="min-w-0">
                <span className="block truncate font-market text-[14px] font-extrabold tracking-[-0.02em] text-white">
                  <ClubName name={clubName} />
                </span>
                <span className="mt-[3px] block font-market text-[8px] font-bold uppercase tracking-[0.2em] text-gold-500/90">
                  {isAdmin ? 'Committee' : 'Member'}
                  {canSwitch ? ' · tap to switch' : ''}
                </span>
              </span>
            </button>

            <button
              type="button"
              className="relative flex-none p-2 text-white/70 transition-colors duration-200 hover:bg-white/[.07] hover:text-gold-500"
              onClick={goToMessages}
              title="Notices"
            >
              <Icon name="bell" size={17} strokeWidth={2} />
              {bellCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 grid h-[16px] min-w-[16px] place-items-center rounded-full bg-flag-500 px-1 font-market text-[8.5px] font-bold text-white">
                  {bellCount}
                </span>
              )}
            </button>

            <button
              type="button"
              className="flex-none"
              onClick={() => {
                navigate(isAdmin ? '/a/profile' : '/m/profile');
                window.scrollTo(0, 0);
              }}
              title={isAdmin ? 'Your committee account' : 'Your profile'}
            >
              {me ? (
                <Avatar member={me} className="h-8 w-8 text-[11px]" />
              ) : (
                <span className="grid h-8 w-8 place-items-center bg-gold-500 font-market text-[11px] font-extrabold text-ink-800">
                  {clubName
                    .split(' ')
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </span>
              )}
            </button>
          </div>
          <AccentRule />
        </header>

        <DemoBanner />

        {/* pb-28 under lg: the chat button floats over the bottom-right
            corner, and with less padding it covered the last button on a
            phone (Change password, Add ground) at the end of the scroll. */}
        <main className="mx-auto w-full max-w-[1180px] flex-1 px-4 pb-28 pt-5 sm:px-8 sm:pt-8 lg:pb-16">
          <Outlet />
        </main>
      </div>

      {/* Chat is only ever available to someone signed in. */}
      {!chatOpen && <ChatFab onOpen={() => setChatOpen(true)} />}
      {chatOpen && <ChatPanel onClose={() => setChatOpen(false)} />}
    </>
  );
}

/** Shown while the club loads. */
export function Loading() {
  const { loading } = useClub();
  return (
    <div className="flex h-[60vh] items-center justify-center font-market text-[13px] text-copy-muted">
      {loading ? 'Loading your club…' : 'Loading…'}
    </div>
  );
}
